import { useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import PortfolioDialog from "@/components/PortfolioDialog";

import solarImg from "@/assets/solar-monitoring.png";
import windImg from "@/assets/industrial-wind.png";
import motorImg from "@/assets/motor-sync.png";
import bfsiImg from "@/assets/BFSI-Architecture.png";
import edtechImg from "@/assets/Education-Architecture.png";

interface SolutionData {
  id: string;
  title: string;
  category: string;
  eyebrow: string;
  subtitle: string;
  businessProblem: string;
  deliverables: {
    title: string;
    description: string;
  }[];
  metrics: {
    value: string;
    label: string;
  }[];
  footer: string;
  image: string;
  architectureDiagram?: string;
}

const solutionsData: Record<string, SolutionData> = {
  // solar: {
  //   id: "solar",
  //   eyebrow: "Solution · Renewable Energy",
  //   category: "Solar Monitoring",
  //   title: "Know your solar plant is failing before it actually does",
  //   subtitle:
  //     "Unplanned outages in solar installations cost operators revenue, maintenance budget, and grid credibility. Our remote monitoring platform turns raw generation data into early warnings — so your team acts on a schedule, not a crisis.",
  //   businessProblem:
  //     "Most solar operators discover faults after generation drops — inverter failures, soiling events, string degradation. By then, the loss has already happened. Dispatching a field team to a remote site without prior diagnosis is slow and expensive.",
  //   deliverables: [
  //     {
  //       title: "Remote visibility, always on",
  //       description:
  //         "Continuous data collection from inverters and meters via OPC UA — no manual readouts, no site visits just to check status.",
  //     },
  //     {
  //       title: "Predictive fault detection",
  //       description:
  //         "Pattern analysis on generation data surfaces anomalies days before they become failures — giving your team time to plan, not react.",
  //     },
  //     {
  //       title: "Actionable maintenance alerts",
  //       description:
  //         "Alerts come with context: which component, what deviation, how urgent. Engineers arrive on-site knowing exactly what to fix.",
  //     },
  //   ],
  //   metrics: [
  //     { value: "72h+", label: "Avg. advance warning before failure" },
  //     { value: "~40%", label: "Reduction in unplanned site visits" },
  //     { value: "24/7", label: "Remote plant visibility" },
  //     { value: "Open", label: "No vendor lock-in" },
  //   ],
  //   footer: "Solar · OPC UA · Predictive Maintenance · Open-source",
  //   image: solarImg,
  //   architectureDiagram: solarImg,
  // },

  // wind: {
  //   id: "wind",
  //   eyebrow: "Solution · Industrial IoT Integration",
  //   category: "Industrial IoT",
  //   title: "Real-time plant data at scale — without the legacy overhead",
  //   subtitle:
  //     "Industrial plants generate enormous volumes of operational data. Legacy OPC UA clients can't keep up, require constant manual intervention, and lock you into proprietary vendors. We replace that bottleneck with a resilient, open-source integration layer.",
  //   businessProblem:
  //     "A 20-year-old proprietary OPC UA client processing a fraction of available data. Every network disruption needed an operator to manually restart the system. Adding new machines meant expensive vendor engagement.",
  //   deliverables: [
  //     {
  //       title: "Massive throughput, no delay",
  //       description:
  //         "Processes 30,000+ data nodes every 2 seconds — dynamically selecting what matters without manual configuration per machine.",
  //     },
  //     {
  //       title: "Self-healing connectivity",
  //       description:
  //         "Automatic recovery from network or server failures. Operations continue without operator intervention.",
  //     },
  //     {
  //       title: "Plug in new assets easily",
  //       description:
  //         "Onboard a new machine or server with a config file update. No code changes, no vendor calls, no downtime.",
  //     },
  //   ],
  //   metrics: [
  //     { value: "30K+", label: "Nodes processed per cycle" },
  //     { value: "2s", label: "Processing interval" },
  //     { value: "~0", label: "Manual recovery incidents" },
  //     { value: "0", label: "Unplanned downtime at go-live" },
  //   ],
  //   footer: "Wind Power · OPC UA · Kafka · Industrial IoT · Open-source",
  //   image: windImg,
  //   architectureDiagram: windImg,
  // },

  motor: {
    id: "motor",
    eyebrow: "Solution · Multi-Vendor Integration",
    category: "Motor Synchronization",
    title:
      "One control layer across every motor on your floor — regardless of who made it",
    subtitle:
      "Industrial facilities run motors from multiple OEMs — each with its own proprietary software. We eliminate operational fragmentation with one unified control architecture.",
    businessProblem:
      "Multi-vendor motor environments force operators to context-switch between incompatible systems. Synchronisation is manual, error-prone, and slow.",
    deliverables: [
      {
        title: "Unified control, any OEM",
        description:
          "OPC UA acts as a common communication layer across all motors regardless of manufacturer.",
      },
      {
        title: "Deterministic synchronisation via TSN",
        description:
          "Time-Sensitive Networking ensures precise shared timing for coordinated motor control.",
      },
      {
        title: "Zero proprietary software dependency",
        description:
          "Fully open-source stack with no vendor licensing fees or software lock-in.",
      },
    ],
    metrics: [
      { value: "Any", label: "OEM supported via OPC UA" },
      { value: "TSN", label: "Deterministic sync precision" },
      { value: "0", label: "Proprietary software licences" },
      { value: "1", label: "Unified control interface" },
    ],
    footer: "Industrial Automation · OPC UA · TSN · Multi-vendor · Open-source",
    image: motorImg,
    architectureDiagram: motorImg,
  },

  bfsi: {
    id: "bfsi",
    eyebrow: "Case Study · BFSI / FinTech",
    category: "Banking Modernization",
    title:
      "From monolith to microservices — zero disruption to live financial operations",
    subtitle:
      "A decade-old monolithic banking system replaced with a resilient, event-driven microservices architecture. Real-time data streaming, hot/warm/cold storage tiering, and full DC/DR failover — built to run without interruption.",
    businessProblem:
      "Legacy monolithic architecture built over 10 years meant every deployment was a risk. A single component failure could bring down unrelated services. Scaling required scaling everything. Audit trails were fragmented, transaction throughput was capped, and disaster recovery was manual and slow.",
    deliverables: [
      {
        title: "Kafka-driven microservices core",
        description:
          "5+ independently deployable microservices connected through Kafka + Kraft, eliminating tight coupling and enabling isolated scaling.",
      },
      {
        title: "3-tier ElasticSearch storage",
        description:
          "Hot, warm, and cold tier architecture reduced long-term storage cost while preserving full transaction searchability.",
      },
      {
        title: "Automated DC/DR resilience",
        description:
          "MirrorMaker 2 replication between DC and DR clusters enabled automated failover with near-zero downtime.",
      },
    ],
    metrics: [
      { value: "5+", label: "Independent microservices" },
      { value: "DC/DR", label: "Dual-cluster failover" },
      { value: "3-tier", label: "ElasticSearch storage" },
      { value: "~0", label: "Downtime during migration" },
    ],
    footer:
      "BFSI · FinTech · Kafka · ElasticSearch · Microservices · DC/DR · Open-source",
    image: bfsiImg,
    architectureDiagram: bfsiImg,
  },

  edtech: {
    id: "edtech",
    eyebrow: "Case Study · EdTech · Mobile App",
    category: "Child-Safe Social Platform",
    title:
      "A safe space for kids to showcase talent, build community, and grow",
    subtitle:
      "A Flutter-based mobile platform for under-18s — combining talent showcase, anonymous community, structured competitions, and cognitive development through Mind Garden. Privacy-first by design, parent-controlled by default.",
    businessProblem:
      "Children with talent had no safe, dedicated platform to share their work. Existing social platforms expose personal information, lack age-appropriate safety controls, and provide no parent oversight or structured growth ecosystem.",
    deliverables: [
      {
        title: "Parental consent + identity safety",
        description:
          "No account activates without verified parent approval. Personal information is protected at API level.",
      },
      {
        title: "Competition + community engine",
        description:
          "Personalised talent feed, structured competitions, automated scoring, and anonymous social interaction.",
      },
      {
        title: "Mind Garden cognitive platform",
        description:
          "Integrated mind games and developmental tracking provide growth beyond content sharing.",
      },
    ],
    metrics: [
      { value: "U18", label: "Exclusively under-18 users" },
      { value: "0", label: "Personal info exposed publicly" },
      { value: "2", label: "Android + iOS platforms" },
      { value: "5", label: "Core modules built" },
    ],
    footer:
      "EdTech · Flutter · Python · Under-18 · Privacy-first · Mobile app · Android · iOS",
    image: edtechImg,
    architectureDiagram: edtechImg,
  },
};

const SolutionDetail = () => {
  // FIXED PARAM NAME
  const { solutionId } = useParams<{ solutionId: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [solutionId]);

  // FIXED LOOKUP
  if (!solutionId || !solutionsData[solutionId]) {
    return <Navigate to="/404" replace />;
  }

  const solutions = solutionsData[solutionId];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 ">
          <img
            src={solutions.image}
            alt={solutions.title}
            className="w-full h-full object-cover bg-gradient-to-r from-black via-black/80 to-black/20"
          />
          <div className="absolute inset-0 bg-black/75" />
        </div>

        <div className="container mx-auto px-6 relative z-10 py-12 md:py-20">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-[0.25em] text-secondary font-semibold mb-4">
              {solutions.eyebrow}
            </p>

            <Badge className="mb-6 bg-white/10 text-white border border-white/20">
              {solutions.category}
            </Badge>

            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
              {solutions.title}
            </h1>

            <p className="text-lg md:text-xl text-slate-200 leading-relaxed max-w-3xl">
              {solutions.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* BUSINESS PROBLEM */}
      <section className="py-8 lg:py-12">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8">
            <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-primary mb-4">
              The problem
            </h3>
            <p className="text-muted-foreground leading-relaxed text-lg">
              {solutions.businessProblem}
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE DELIVER */}
      <section className="pb-12">
        <div className="container mx-auto px-6 max-w-6xl">
          <p className="text-sm uppercase tracking-[0.25em] text-slate-400 font-bold mb-8">
            What we deliver
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {solutions.deliverables.map((item, index) => (
              <Card
                key={index}
                className="border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition"
              >
                <CardHeader>
                  <CardTitle
                    className={`text-lg font-semibold ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                  >
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <p className="text-slate-600 leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="pb-12">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 rounded-2xl overflow-hidden">
            {solutions.metrics.map((metric, index) => (
              <div
                key={index}
                className={`p-8 border-b md:border-b-0 md:border-r last:border-r-0 border-border bg-card shadow-sm`}
              >
                <div
                  className={`text-4xl text-center font-bold ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} mb-2`}
                >
                  {metric.value}
                </div>
                <div className="text-sm text-center text-slate-500">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {solutions.architectureDiagram && (
        <section className="pb-12">
          <div className="container mx-auto px-6 max-w-7xl">
            <div className="text-center mb-10">
              <p className="text-sm uppercase tracking-[0.25em] text-slate-400 font-bold mb-3">
                System Architecture
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Full implementation blueprint
              </h2>
            </div>

            <div className="bg-card border border-border rounded-3xl p-4 md:p-8 shadow-elegant overflow-hidden">
              <img
                src={solutions.architectureDiagram}
                alt={`${solutions.title} Architecture Diagram`}
                className="w-full h-auto object-contain rounded-2xl"
              />
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="pb-12">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="bg-card border border-border rounded-3xl p-10 text-center shadow-elegant">
            <p className="text-sm uppercase tracking-[0.25em] text-slate-400 mb-4">
              {solutions.footer}
            </p>

            <h2 className="text-3xl font-bold mb-4">
              Ready to implement this solution?
            </h2>

            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              We design scalable, open, enterprise-grade systems that solve real
              operational bottlenecks.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <GetStartedDialog
                title={`Get Started with ${solutions.category}`}
                description={`Discuss your ${solutions.category} implementation with our experts.`}
                category={solutions.category}
                buttonText="Get Started"
              />

              <PortfolioDialog buttonText="View Portfolio" />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12 md:pb-16">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.25em] text-slate-400 font-bold mb-3">
              Explore More Solutions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Built for real industrial bottlenecks
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              From renewable energy monitoring to industrial automation, our
              systems are engineered for measurable operational impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {Object.values(solutionsData).map((solution) => (
              <div
                key={solution.id}
                onClick={() => {
                  if (solution.id !== solutionId) {
                    window.location.href = `/solutions/${solution.id}`;
                  }
                }}
                className={`group relative rounded-3xl overflow-hidden min-h-[420px] cursor-pointer transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${
                  solution.id === solutionId
                    ? "ring-4 ring-primary cursor-default"
                    : ""
                }`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
                </div>

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-end p-8 text-white">
                  <Badge className="mb-4 w-fit bg-white/10 border border-white/20 text-white">
                    {solution.category}
                  </Badge>

                  <h3 className="text-2xl font-bold leading-tight mb-3 group-hover:text-secondary transition-colors">
                    {solution.title}
                  </h3>

                  <p className="text-slate-200 text-sm leading-relaxed line-clamp-4 mb-6">
                    {solution.subtitle}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-secondary">
                      {solution.id === solutionId
                        ? "Currently Viewing"
                        : "View Solution →"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionDetail;
